import { FunctionComponent, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { Loader } from "../../../components/loader";
import { useHasPermission } from "../../../components/permission";
import { Table } from "../../../components/table";
import { useFetchable } from "../../../store/common/fetchable";
import { isFetchableSuccess } from "../../../store/common/fetchable/helpers";
import { UserActions } from "../../../store/users/actions";
import { UsersDTO } from "../../../store/users/models/dto";
import { Users } from "../../../store/users/reducer";
import { TwoFactorDetails } from "./user/2fa";
import { UserAccountDetails } from "./common/account-details";
import { StyledWidget } from "./common/components";
import { ContactDetails } from "./user/contact-details";
import { DelegateUsers } from "./merchant/delegate-users";
import { UserOverview } from "./user/overview";
import { SubMerchants } from "./merchant/sub-merchants";
import { MerchantOverview } from "./merchant/overview";
import { DelegateOverview } from "./delegate/overview";
import { Tabs } from "../../../components/tabs/tabs-page";
import styled from "styled-components";
import { Wallets } from "./user/wallets";

const StyledTabs = styled(Tabs)`
  margin-bottom: 1em;
`;

export const InnerUserDetail: FunctionComponent<{
  item: UsersDTO.Responses.GetSingle.T;
}> = ({ item }) => {
  const { wallets } = item;

  const detailed = useHasPermission(["ROLE_ADMIN"]);

  const isMerchant = useMemo(
    () => item.roleDetails.roles.includes("ROLE_MERCHANT"),
    [item]
  );

  const tabs = useMemo(
    () =>
      isMerchant
        ? [
          ...[
            {
              title: "User Account Details",
              content: <UserAccountDetails item={item} />,
            },
            {
              title: "Delegate Users",
              content: <DelegateUsers item={item} />,
            },
          ],
          ...(detailed
            ? [
              {
                title: "Sub Merchants",
                content: <SubMerchants item={item} />,
              },
            ]
            : []),
        ]
        : [],
    [detailed, isMerchant, item]
  );

  if (isMerchant) {
    return (
      <>
        <MerchantOverview item={item} />
        <StyledTabs noMargin tabs={tabs} />
        <TwoFactorDetails item={item} />
      </>
    );
  } else if (item.roleDetails.delegate) {
    return (
      <>
        <DelegateOverview item={item} />
        <UserAccountDetails item={item} />
        <TwoFactorDetails item={item} />
      </>
    );
  }

  return (
    <>
      <UserOverview item={item} />
      <UserAccountDetails item={item} />
      <TwoFactorDetails item={item} />
      {detailed ? <ContactDetails item={item} /> : null}
      <Wallets item={item} />
      {/* <StyledWidget title="Wallets">
        <Table
          data={wallets}
          head={[
            { name: "address", title: "Address" },
            { name: "chain", title: "Chain" },
          ]}
          unsortable={["address", "chain"]}
        ></Table>
      </StyledWidget> */}
    </>
  );
};

export const UserDetail: FunctionComponent<{ id: string }> = ({ id }) => {
  const data = useFetchable(
    Users.singleUser,
    useMemo(() => ({ userId: id }), [id])
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (isFetchableSuccess(data) && data.data.userReferenceId !== id) {
      dispatch(UserActions.getSingleUser({ userId: id }));
    }
  }, [id]);

  return (
    <Loader data={[data]}>
      {([resolvedData]) => <InnerUserDetail item={resolvedData} />}
    </Loader>
  );
};
